import React from 'react';
import { Box, Typography, Stack, Button, Chip, CircularProgress } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import VoicemailIcon from '@mui/icons-material/Voicemail';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { ProviderOutreach, ProviderOutreachStatus } from '~/types/coordinate';

const STATUS_CONFIG: Record<ProviderOutreachStatus, { icon: React.ReactNode; label: string; color: string; bgcolor: string }> = {
  calling:        { icon: <CircularProgress size={12} />, label: 'Calling…', color: '#1565c0', bgcolor: '#e3f2fd' },
  responded_yes:  { icon: <CheckCircleIcon sx={{ fontSize: 14 }} />, label: 'Available', color: '#006e60', bgcolor: '#e0f7f4' },
  responded_no:   { icon: <CancelIcon sx={{ fontSize: 14 }} />, label: 'Unavailable', color: '#b71c1c', bgcolor: '#ffebee' },
  voicemail:      { icon: <VoicemailIcon sx={{ fontSize: 14 }} />, label: 'Voicemail', color: '#e65100', bgcolor: '#fff3e0' },
  no_answer:      { icon: <PhoneInTalkIcon sx={{ fontSize: 14 }} />, label: 'No answer', color: '#757575', bgcolor: '#f6f6f6' },
};

interface Props {
  outreach: ProviderOutreach;
  selectedProviderId?: string;
  onSelectProvider: (providerId: string) => void;
  onConfirmProvider: () => void;
}

const ProviderOutreachPanel: React.FC<Props> = ({ outreach, selectedProviderId, onSelectProvider, onConfirmProvider }) => {
  const allDone = outreach.attempts.every(a => a.status !== 'calling');
  const anyAvailable = outreach.attempts.some(a => a.status === 'responded_yes');

  // Responded yes floated to top
  const sortedAttempts = [...outreach.attempts].sort((a, b) => {
    if (a.status === 'responded_yes') return -1;
    if (b.status === 'responded_yes') return 1;
    return 0;
  });

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'rgba(99, 95, 153, 0.2)',
        borderRadius: 1.5,
        bgcolor: '#f0eef8',
        p: 2,
        mb: 2,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <AutoAwesomeIcon sx={{ fontSize: 15, color: '#635f99' }} />
        <Typography variant="caption" fontWeight={700} color="#635f99">
          Coordinate AI — Provider Outreach
        </Typography>
        {!allDone && (
          <Chip
            label="Calls in progress"
            size="small"
            sx={{ height: 16, fontSize: '0.6rem', fontWeight: 700, bgcolor: '#e3f2fd', color: '#1565c0' }}
          />
        )}
        {allDone && (
          <Chip
            label="All calls complete"
            size="small"
            sx={{ height: 16, fontSize: '0.6rem', fontWeight: 700, bgcolor: '#e0f7f4', color: '#006e60' }}
          />
        )}
      </Stack>

      {allDone && anyAvailable && (
        <Box sx={{ mb: 1.5, px: 1.5, py: 1, bgcolor: '#e0f7f4', borderRadius: 1, border: '1px solid rgba(0,189,161,0.2)' }}>
          <Typography variant="caption" color="#006e60" fontWeight={600}>
            Provider available — select one below and confirm to proceed
          </Typography>
        </Box>
      )}

      <Stack spacing={1}>
        {sortedAttempts.map(attempt => {
          const cfg = STATUS_CONFIG[attempt.status];
          const isSelected = selectedProviderId === attempt.providerId;
          const isAvailable = attempt.status === 'responded_yes';
          return (
            <Box
              key={attempt.providerId}
              sx={{
                bgcolor: isAvailable ? (isSelected ? '#e0f7f4' : '#edfaf8') : '#fff',
                border: '1px solid',
                borderColor: isSelected ? '#00bda1' : isAvailable ? 'rgba(67,160,71,0.35)' : 'rgba(123,31,162,0.12)',
                borderRadius: 1,
                p: 1.5,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.5,
                cursor: isAvailable ? 'pointer' : 'default',
                transition: 'border-color 0.15s, background-color 0.15s',
                '&:hover': isAvailable ? { borderColor: '#00bda1', bgcolor: '#e0f7f4' } : {},
              }}
              onClick={() => isAvailable && onSelectProvider(attempt.providerId)}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.25 }}>
                  <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.82rem' }}>
                    {attempt.providerName}
                  </Typography>
                  <Chip
                    icon={<Box sx={{ display: 'flex', color: cfg.color }}>{cfg.icon}</Box>}
                    label={cfg.label}
                    size="small"
                    sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, bgcolor: cfg.bgcolor, color: cfg.color, '& .MuiChip-icon': { ml: 0.5 } }}
                  />
                </Stack>
                {attempt.response && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem', lineHeight: 1.4 }}>
                    {attempt.response}
                  </Typography>
                )}
              </Box>

              {/* Inline confirm CTA for available providers */}
              {isAvailable && allDone && (
                <Button
                  variant={isSelected ? 'contained' : 'outlined'}
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelectProvider(attempt.providerId);
                    onConfirmProvider();
                  }}
                  sx={{
                    flexShrink: 0,
                    fontSize: '0.72rem',
                    ...(isSelected
                      ? { bgcolor: '#006e60', '&:hover': { bgcolor: '#005a4e' } }
                      : { borderColor: '#00bda1', color: '#006e60', '&:hover': { borderColor: '#006e60', bgcolor: 'rgba(0,189,161,0.04)' } }),
                  }}
                >
                  {isSelected ? 'Confirm' : 'Select & Confirm'}
                </Button>
              )}
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
};

export default ProviderOutreachPanel;
